'use client';

import { usePortfolios } from '@/domain/portfolio/hooks';
import { type RoadmapPortfolioDTO } from '@/domain/portfolio/types';
import { type RoadMapUpdate } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Grid } from '@andritzot/metris-web-ui/layout/grid';
import ClearIcon from '@mui/icons-material/Clear';
import { createDataTree } from '@progress/kendo-react-treelist';
import { type MenuItem } from 'primereact/menuitem';
import { TieredMenu } from 'primereact/tieredmenu';
import { useMemo, useRef } from 'react';
import { type Control, Controller } from 'react-hook-form';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

interface PortfolioFieldProps {
  viewOnly: boolean;
  control: Control<RoadMapUpdate, unknown>;
  isCustomerCare: boolean;
}

export interface PortfolioTree extends RoadmapPortfolioDTO {
  portfolios: PortfolioTree[];
}

const generateItems = (
  treeData: PortfolioTree[],
  onSelect: (id: number) => void,
  isCustomerCare: boolean,
): MenuItem[] =>
  treeData.map((p) => ({
    id: `${p.idPortfolio}`,
    label: p.name,
    disabled: p.locked && !isCustomerCare,
    ...(p.portfolios?.length
      ? { items: generateItems(p.portfolios, onSelect, isCustomerCare) }
      : {
          command: () => {
            if (!p.locked || isCustomerCare) onSelect(p.idPortfolio);
          },
        }),
  }));

export function findPortfolioPath(portfolioItems: RoadmapPortfolioDTO[], targetId: number): string {
  const path = getPathRecursive(portfolioItems, targetId);
  return path?.join(' → ') ?? '';
}

const getPathRecursive = (portfolioItems: RoadmapPortfolioDTO[], targetId: number): string[] | null => {
  const item = portfolioItems.find((portfolio) => portfolio.idPortfolio === targetId);

  if (!item) return null;

  const path: string[] = [item.name];

  if (item.idParent !== null) {
    const parentPath = getPathRecursive(portfolioItems, item.idParent ?? 0);
    if (parentPath === null) return path;
    return [...parentPath, ...path];
  }

  return path;
};
interface PortfolioMenuProps {
  value: number | undefined;
  onChange: (id: number | null) => void;
  isCustomerCare: boolean;
  disabled?: boolean;
}

function PortfolioMenuField({ value, onChange, isCustomerCare, disabled }: Readonly<PortfolioMenuProps>) {
  const menu = useRef<TieredMenu>(null);
  const { data = [] } = usePortfolios();

  const items = useMemo((): MenuItem[] => {
    const treeData: PortfolioTree[] = createDataTree(
      data,
      (i: PortfolioTree) => i.idPortfolio,
      (i: PortfolioTree) => i.idParent,
      'portfolios',
    );
    return generateItems(treeData, (id) => onChange(id), isCustomerCare);
  }, [data, isCustomerCare, onChange]);

  const label = value ? findPortfolioPath(data, value) : '';

  return (
    <Grid container size={12}>
      <Grid size={2}>
        <TieredMenu model={items} popup ref={menu} breakpoint="767px" />
        <Button
          variant={value ? 'outlined' : 'contained'}
          sx={{ textAlign: 'left' }}
          onClick={(e) => menu.current?.toggle(e)}
          disabled={disabled}
        >
          {value ? 'Update Portfolio' : 'Select Portfolio'}
        </Button>
      </Grid>
      <Grid size={10}>
        <Typography variant="body2" color="text.secondary">
          {label}
          {value != null && !disabled && (
            <Button variant="text" color="error" size="small" onClick={() => onChange(null)}>
              <ClearIcon fontSize="small" />
            </Button>
          )}
        </Typography>
      </Grid>
    </Grid>
  );
}

export function PortfolioField({ viewOnly, control, isCustomerCare }: Readonly<PortfolioFieldProps>) {
  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);
  const { data = [] } = usePortfolios();

  const portfolioPath = editableRoadMap.portfolioId ? findPortfolioPath(data, editableRoadMap.portfolioId) : '';

  return (
    <Controller
      name="portfolioId"
      control={control}
      render={({ field: { value, onChange } }) => (
        <FieldActionsWrapper
          locked={editableRoadMap.locked}
          lockedTooltip="This item is scope locked. You can't change the portfolio. To unlock it contact an admin!"
          copyValue={portfolioPath}
        >
          <PortfolioMenuField
            value={value}
            onChange={(id) => onChange(id)}
            isCustomerCare={isCustomerCare}
            disabled={viewOnly || editableRoadMap.locked}
          />
        </FieldActionsWrapper>
      )}
    />
  );
}
